import { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { numericValue } from '@utils';

import './goods.scss';

const Goods = ({ gradeItems, productsItems, capacity, changeCopacity, products, addProduct, removeProduct }) => {
  const [windowWidth, setWindowWidth] = useState(window?.innerWidth);
  const toast = useRef(null);
  const GoodsSchema = Yup.object().shape({
    capacity: Yup.number().required("Обов'язкове поле").min(0, 'Занадто мало!').max(100000, 'Занадто багато!'),
    grade: Yup.string().required("Обов'язкове поле"),
    length: Yup.string().required("Обов'язкове поле").min(0, 'Занадто мало!').max(9, 'Занадто багато!'),
    product: Yup.string().required("Обов'язкове поле"),
    barcode: Yup.string().required("Обов'язкове поле").min(3, 'Занадто короткий!').max(50, 'Занадто довгий!'),
  });

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };
  const calculateCustomValue = (products, capacity) => () => {
    if (products?.length) {
      return (capacity / products?.length).toFixed(2);
    }
    return 0;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const { length, grade, product, barcode, capacity } = values;

    if (products.some((item) => item?.barcode.trim().toUpperCase() === barcode.trim().toUpperCase())) {
      toast.current.show({
        severity: 'error',
        summary: `Не можливо додати!`,
        detail: `Номер бирки уже існує!`,
        life: 3000,
      });

      return null;
    }

    const gradeFind = gradeItems.find((item) => item.value === grade) ?? {};
    const productFind = productsItems.find((item) => item.value === product) ?? {};

    addProduct({
      id: Math.random(),
      length,
      barcode,
      grade: { title: gradeFind.label, _id: gradeFind.value },
      product: { title: productFind.label, _id: productFind.value },
    });

    resetForm({ values: { capacity, length, barcode: '', grade, product } });
    setSubmitting(false);
  };

  const actionBodyTemplate = ({ id, date_inventory }) => {
    if (date_inventory) {
      return null;
    }
    return <Button icon='pi pi-trash' rounded outlined severity='danger' onClick={() => removeProduct(id)} />;
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <div className='goods'>
        <Formik
          initialValues={{
            capacity: capacity,
            grade: '',
            length: '',
            product: '',
            barcode: '',
          }}
          validationSchema={GoodsSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form>
              <div className='m-0 form'>
                <div className='flex align-items-start justify-content-between gap-3 form__elem-wrap'>
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field
                        name='capacity'
                        as={InputNumber}
                        onChange={(e) => {
                          setFieldValue('capacity', e.value);
                          changeCopacity(e.value);
                        }}
                        value={capacity}
                        useGrouping={false}
                        suffix=' м³'
                        minFractionDigits={2}
                        className={errors.capacity && touched.capacity ? 'p-invalid' : ''}
                      />
                      <label htmlFor='capacity'>Загальний об’єм в м/куб</label>
                    </span>
                    {errors.capacity && touched.capacity ? (
                      <small id='capacity-help' className='p-error'>
                        {errors.capacity}
                      </small>
                    ) : null}
                  </div>
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field name='grade' className={errors.grade && touched.grade ? 'p-invalid' : ''}>
                        {({ field }) => (
                          <Dropdown
                            value={field.value}
                            filter
                            options={gradeItems ?? []}
                            onChange={(e) => setFieldValue('grade', e.value)}
                            emptyMessage='Немає деревини'
                          />
                        )}
                      </Field>
                      <label htmlFor='grade'>Сорт деревини</label>
                    </span>
                    {errors.grade && touched.grade ? (
                      <small id='grade-help' className='p-error'>
                        {errors.grade}
                      </small>
                    ) : null}
                  </div>
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field
                        name='length'
                        as={InputText}
                        onChange={(e) => setFieldValue('length', numericValue(e.target.value))}
                        useGrouping={false}
                        minFractionDigits={2}
                        className={errors.length && touched.length ? 'p-invalid' : ''}
                      />
                      <label htmlFor='length'>Довжина в метрах</label>
                    </span>
                    {errors.length && touched.length ? (
                      <small id='length-help' className='p-error'>
                        {errors.length}
                      </small>
                    ) : null}
                  </div>
                </div>
                <div className='flex align-items-start justify-content-between gap-3 form__elem-wrap'>
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field name='product' className={errors.product && touched.product ? 'p-invalid' : ''}>
                        {({ field }) => (
                          <Dropdown
                            value={field.value}
                            filter
                            options={productsItems ?? []}
                            onChange={(e) => setFieldValue('product', e.value)}
                            emptyMessage='Немає перевізників'
                          />
                        )}
                      </Field>
                      <label htmlFor='product'>Продукція</label>
                    </span>
                    {errors.product && touched.product ? (
                      <small id='product-help' className='p-error'>
                        {errors.product}
                      </small>
                    ) : null}
                  </div>
                  <div className='form__elem'>
                    <span className='p-float-label'>
                      <Field
                        name='barcode'
                        as={InputText}
                        useGrouping={false}
                        className={errors.barcode && touched.barcode ? 'p-invalid' : ''}
                      />
                      <label htmlFor='barcode'>Номер бирки</label>
                    </span>
                    {errors.barcode && touched.barcode ? (
                      <small id='barcode-help' className='p-error'>
                        {errors.barcode}
                      </small>
                    ) : null}
                  </div>
                </div>
                <Button type='submit' className='form__button' label='Додати продукт' icon='pi' severity='secondary' />
              </div>
            </Form>
          )}
        </Formik>

        <DataTable
          value={products}
          responsiveLayout='scroll'
          scrollable
          scrollHeight='400px'
          emptyMessage='Поки що немає даних'
        >
          <Column field={'barcode'} header={'Номер бирки'} />
          <Column field={'product.title'} header={'Продукція'} />
          <Column field={'grade.title'} header={'Сорт'} />
          <Column field={'length'} header={'Довжина в м.'} />
          <Column body={calculateCustomValue(products, capacity)} header={"Об'єм"} />
          <Column body={actionBodyTemplate} frozen={windowWidth > 600} alignFrozen='right'></Column>
        </DataTable>
      </div>
      <Toast ref={toast} />
    </>
  );
};

export default Goods;
